import layout from './Chips.html?raw';
import Control from '@/Controls/Control';
import ComponentManager from '@/Management/ComponentManager';
import ChipsViewModelType from '@/ViewModels/Controls/ChipsViewModel';
import Utilities from '@/Utility/Utilities';
import { Dictionary } from '#/DefaultViewExtensionInterfaces';
import LocatorHelper from '@/Utility/LocatorHelper';

export default class Chips extends Control {

    protected jQueryControl: JQuery;

    public constructor(private viewModel: ChipsViewModelType<any>, protected element?: JQuery) {
        super(viewModel, element);

        this.onWidgetCreated(this.onWidgetCreatedCallback.bind(this));
    }

    public getControl(): JQuery {
        if (this.jQueryControl != null) {
            return this.jQueryControl;
        }

        this.jQueryControl = this.element || $('<div>');

        let timerId = this.applyTemplateAsync(layout, this.jQueryControl, 10);
        $.when(this.triggers[timerId]).then(() => {
            if (this.isDisposed)
                return;

            this.widget(null);
            this.viewModel.isComponentReady(true);
        });

        return this.jQueryControl;
    }

    private onWidgetCreatedCallback(widget: null): void {
        this.applyBindings(this.jQueryControl, {
            controlName: 'chips',

            loader: this.viewModel.isBusy,

            controlIdent: this.identifier,

            visible: this.viewModel.isVisible,

            invalid: this.viewModel.invalidationText,

            tooltip: this.viewModel.tooltip,

            focus: this.viewModel.isFocusable,

            value: this.viewModel.value,

            states: this.definePureComputed({
                read: () => {
                    var states = [];

                    if (this.viewModel.isRequired() === true) {
                        states.push('required');
                    }

                    if (this.viewModel.isValid() === true) {
                        states.push('valid');
                    } else {
                        states.push('invalid');
                    }

                    if (this.viewModel.isEnabled() === true) {
                        states.push('enabled');
                    } else {
                        states.push('disabled');
                    }

                    return states;
                }
            })
        });

        if (this.viewModel.onKeyUp != null) {
            this.addEventHandler('keyup', this.jQueryControl, (event: JQuery.Event) => {
                if (!this.viewModel.isEnabled()) {
                    return;
                }

                this.viewModel.onKeyUp(event.keyCode);
            }, 'input');
        }

        if (this.viewModel.onBlur != null) {
            this.addEventHandler('blur', this.jQueryControl, () => {
                if (!this.viewModel.isEnabled()) {
                    return;
                }

                this.viewModel.onBlur();

            }, 'input');
        }

        if (this.viewModel.onClick) {
            this.addEventHandler('click', this.jQueryControl, (event: JQuery.ClickEvent) => {
                if (!this.viewModel.isEnabled()) {
                    return;
                }

                let $element = $(event.target);
                if ($element.hasClass('remove')) {
                    return;
                }

                let $textSpan = $element.hasClass('text') ? $element : $element.find('.text');
                let name = $textSpan.attr('id')

                this.viewModel.onClick(name);

                // update activeChips
                let isActiveChip = _.contains(this.viewModel.activeChips(), name);
                if (isActiveChip) {
                    this.viewModel.activeChips(this.viewModel.activeChips().filter(anyName => anyName != name)); // remove
                }
                else {
                    this.viewModel.activeChips.push(name); // add
                }

            }, '.chip');
        }

        this.defineSubscription(this.viewModel.activeChips, (activeChipNames: Array<string>) => {
            this.updateActiveMarkup(activeChipNames);
        })(this.viewModel.activeChips());

        this.defineSubscription(this.viewModel.tooltips, (tooltips: Dictionary<string>) => {
            this.updateTooltips(tooltips);
        })(this.viewModel.tooltips());
    }

    private afterRenderingChips() {
        this.addLocators();
        this.updateActiveMarkup(this.viewModel.activeChips());
        this.updateTooltips(this.viewModel.tooltips());
    }

    private updateTooltips(tooltips: Dictionary<string> = {}) {
        this.jQueryControl.find('.chip').each((index: number, element: HTMLElement) => {
            let id = $(element).find('.text').attr('id');
            let tooltip = tooltips[id];

            ko.applyBindingsToNode(element, {
                tooltip: tooltip
            });

            // TODO what about disposing this binding?
        });
    }

    private updateActiveMarkup(activeChipNames: Array<string> = []) {
        this.jQueryControl.find('.chip').each((index: number, element: HTMLElement) => {
            let id = $(element).find('.text').attr('id');
            if (_.contains(activeChipNames, id)) {
                $(element).addClass('active-chip');
            } else {
                $(element).removeClass('active-chip');
            }
        });
    }

    private addLocators() {
        this.jQueryControl.find('.chip').each((index: number, element: HTMLElement) => {
            let $element = $(element);

            this.applyBindings($element, {
                locator: LocatorHelper.buildLocator(this.viewModel.locator, index.toString())
            });
            this.applyBindings($element.find('.text'), {
                locator: LocatorHelper.buildLocator(this.viewModel.locator, index.toString(), 'label')
            });
            this.applyBindings($element.find('.remove'), {
                locator: LocatorHelper.buildLocator(this.viewModel.locator, index.toString(), 'remove')
            });
        });
    }
}

ComponentManager.setControl('Chips', Chips, 'RADPresentation.HTMLClient.DefaultViewExtensions');